import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toInteger } from 'lodash';
import {
  FilterTypes,
  INTIAL_FILTERS,
  RANGE_FILTERS,
  SINGLE_FILTERS,
} from '../constants/initialFilters';
import { filterCurrentState } from '../utils/analyticsEventListeners';
import { RootState } from '.';
import { NonAudiCountType } from '../types';
import buildQueryParams from '../hooks/useUpdateQueryParams';

interface FiltersReducerTypes {
  confirmedFilters: FilterTypes;
  temporaryFilters: FilterTypes;
  availableFilters: any;
  nonAudiCounts: {
    nonAudiManufactures: NonAudiCountType[] | [];
    nonAudiModels: NonAudiCountType[] | [];
  };
}

type SigleFilterAddActionType = {
  [key: string]: string;
};

type RangeFilterAddActionType = {
  [key: string]: number[];
};

const initialState: FiltersReducerTypes = {
  confirmedFilters: INTIAL_FILTERS,
  temporaryFilters: INTIAL_FILTERS,
  availableFilters: {},
  nonAudiCounts: {
    nonAudiManufactures: [],
    nonAudiModels: [],
  },
};

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    // Initially set filters from LS to Redux
    setFiltersFromLS: (state: FiltersReducerTypes, action: PayloadAction<FilterTypes>) => {
      return { ...state, temporaryFilters: action.payload, confirmedFilters: action.payload };
    },

    setNonAudiCounts: (state: FiltersReducerTypes, action) => {
      return { ...state, nonAudiCounts: action.payload };
    },

    // Initially set available filters from LS to Redux
    setAvailableFilters: (state, action) => {
      return { ...state, availableFilters: action.payload };
    },

    setMainDealerId: (state, action) => {
      const mainDealerId = action.payload;
      const newState = {
        ...state,
        confirmedFilters: {
          ...state.confirmedFilters,
          partnerDealers: [mainDealerId],
        },
        temporaryFilters: {
          ...state.temporaryFilters,
          partnerDealers: [mainDealerId],
        },
      };
      return newState;
    },

    changeRangeTempFilter: (state, action: PayloadAction<RangeFilterAddActionType>) => {
      const filterType = Object.keys(action.payload)[0]; // Access the first key (filterType)
      const filterValue = action.payload[filterType]; // Access the value associated with filterType
      if (
        filterType === 'power' ||
        filterType === 'mileage' ||
        filterType === 'modelYear' ||
        filterType === 'price'
      ) {
        return {
          ...state,
          temporaryFilters: {
            ...state.temporaryFilters,
            [filterType]: filterValue,
          },
        };
      }
      return { ...state };
    },

    // Remove temp filter from filter menu
    addOrRemoveTempFilter: (state, action: PayloadAction<SigleFilterAddActionType>) => {
      const { filterType, filterValue } = action.payload;
      const filterArray = (state.temporaryFilters as any)[filterType];

      if (filterType === 'otherMakes' && filterArray.includes(filterValue)) {
        const convertedModelsToMakes = state.temporaryFilters.nonAudiModels.filter(
          (model) => model.slice(0, 2) !== filterValue,
        );
        return {
          ...state,
          temporaryFilters: {
            ...state.temporaryFilters,
            nonAudiModels: convertedModelsToMakes,
            [filterType]: (state.temporaryFilters as any)[filterType].filter(
              (item: string) => item !== filterValue,
            ),
          },
        };
      }

      if (filterType === 'nonAudi') {
        return {
          ...state,
          temporaryFilters: {
            ...state.temporaryFilters,
            nonAudi: filterValue,
            usedInventoryType: '',
          },
        };
      }

      if (
        filterType === 'vehicleType' ||
        filterType === 'usedInventoryType' ||
        filterType === 'availableInMonths'
      )
        return {
          ...state,
          temporaryFilters: {
            ...state.temporaryFilters,
            [filterType]: filterType === 'availableInMonths' ? toInteger(filterValue) : filterValue,
            nonAudi: '',
          },
        };
      if (filterArray.includes(filterValue)) {
        return {
          ...state,
          temporaryFilters: {
            ...state.temporaryFilters,
            [filterType]: (state.temporaryFilters as any)[filterType].filter(
              (item: string) => item !== filterValue,
            ),
          },
        };
      }

      return {
        ...state,
        temporaryFilters: {
          ...state.temporaryFilters,
          [filterType]: [...(state.temporaryFilters as any)[filterType], filterValue],
        },
      };
    },

    // Remove filter from everywhere (filter tags and filter menu) + upload fresh filters to LS
    removeFilter: (state, action) => {
      const { filterType, filterValue, isTier3page, isUrlParamsDynamic } = action.payload;
      if (isTier3page && filterType === 'nonAudi') {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            usedInventoryType: '',
            [filterType]: filterValue,
          },
          temporaryFilters: {
            ...state.temporaryFilters,
            usedInventoryType: '',
            [filterType]: filterValue,
          },
        };
        if (isTier3page && isUrlParamsDynamic) {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      if (SINGLE_FILTERS.includes(filterType)) {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: filterValue,
          },
          temporaryFilters: {
            ...state.temporaryFilters,
            [filterType]: filterValue,
          },
        };
        if (!isTier3page) {
          localStorage.setItem('selectedFilters-v4', JSON.stringify(newState.confirmedFilters));
        }
        if (isTier3page && isUrlParamsDynamic) {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      if (RANGE_FILTERS.includes(filterType)) {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: [],
          },
          temporaryFilters: {
            ...state.temporaryFilters,
            [filterType]: [],
          },
        };
        if (!isTier3page) {
          localStorage.setItem('selectedFilters-v4', JSON.stringify(newState.confirmedFilters));
        }
        if (isTier3page && isUrlParamsDynamic) {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      const newState = {
        ...state,
        confirmedFilters: {
          ...state.confirmedFilters,
          [filterType]: (state.confirmedFilters as any)[filterType].filter(
            (item: string) => item !== filterValue,
          ),
        },
        temporaryFilters: {
          ...state.temporaryFilters,
          [filterType]: (state.temporaryFilters as any)[filterType].filter(
            (item: string) => item !== filterValue,
          ),
        },
      };
      // call filter current state(Data Layer events) when a filter gets removed from filters
      filterCurrentState(newState.confirmedFilters);
      if (!isTier3page) {
        localStorage.setItem('selectedFilters-v4', JSON.stringify(newState.confirmedFilters));
      }
      if (isTier3page && isUrlParamsDynamic) {
        buildQueryParams(newState.confirmedFilters);
      }
      return newState;
    },

    // Apply filter from filters menu
    setConfirmaedFilters: (state, action) => {
      // Do not add partnerDealers to localStorage
      const { temporaryFilters, isTier3page } = action.payload;
      if (!isTier3page) {
        localStorage.setItem('selectedFilters-v4', JSON.stringify(temporaryFilters));
      }
      return { ...state, confirmedFilters: temporaryFilters };
    },

    // Remove ALL filters from everywhere and set initial ones.
    clearAllFilters: (state) => {
      // Do not add partnerDealers to localStorage
      const { ...payloadForLS } = INTIAL_FILTERS;
      localStorage.setItem('selectedFilters-v4', JSON.stringify(payloadForLS));
      return { ...state, confirmedFilters: INTIAL_FILTERS, temporaryFilters: INTIAL_FILTERS };
    },

    setDefaultTier3Filters: (state, action) => {
      return { ...state, confirmedFilters: action.payload, temporaryFilters: action.payload };
    },
  },
});

export const selectFilters = (state: RootState) => state.filters;

export const {
  setFiltersFromLS,
  setNonAudiCounts,
  setAvailableFilters,
  addOrRemoveTempFilter,
  changeRangeTempFilter,
  removeFilter,
  setConfirmaedFilters,
  clearAllFilters,
  setMainDealerId,
  setDefaultTier3Filters,
} = filterSlice.actions;

export default filterSlice.reducer;
